import { useQuery } from '@tanstack/react-query';
import { getAccess, ANONYMOUS, type Access } from '@/services/access';

/**
 * useAccess — o único sítio onde a UI pergunta "posso?".
 *
 * A resposta vem do servidor (/access/me), que a resolveu na matriz em BD. A UI
 * não recalcula RBAC: se o botão aparece, o backend também deixa passar — e
 * vice-versa. É isto que mata a classe de bug "vejo o ecrã mas dá 403".
 *
 * `can()` aceita a permissão canónica (ex.: 'org.sales.view'), nunca um papel.
 */
export function useAccess() {
  const q = useQuery({
    queryKey: ['access', 'me'],
    queryFn: getAccess,
    staleTime: 60_000,
  });
  const access: Access = q.data ?? ANONYMOUS;
  const granted = new Set(access.permissions);

  return {
    loading: q.isLoading,
    access,
    /** Tem esta permissão? (a permissão já vem resolvida com o escopo do servidor) */
    can: (permission: string) => granted.has(permission),
    /** Tem pelo menos uma destas? */
    canAny: (...permissions: string[]) => permissions.some((p) => granted.has(p)),
    /** Equipa Marcianus — eixo A. Não confundir com "admin de uma org". */
    isStaff: access.isStaff,
    /** Membro de ≥1 org — eixo B. É isto que faz aparecer a Consola do organizador. */
    isOrganizer: access.isOrganizer,
    /** A org sobre a qual estas permissões foram calculadas (a de casa, por omissão). */
    activeOrgId: access.activeOrgId ?? null,
    /** Está a ser impersonado por um staff → a UI TEM de mostrar o banner. */
    impersonatedBy: access.impersonatedBy ?? null,
  };
}
